import { Component, Input, OnInit, ViewChild } from '@angular/core'
import { debounce } from 'lodash'
import moment from 'moment-timezone'
import { CommService } from 'src/app/core/comm.service'
import { TerminologyService } from 'src/app/terminology.service'
import { TimezoneService } from 'src/app/timezone.service'
import { TrendsService } from 'src/app/trends.service'
import { ScannerMultiSelectComponent } from '../scanner-multi-select/scanner-multi-select.component'
import { SideControlsComponent } from '../side-controls/side-controls.component'
import {
  CSVDemoData,
  DEFAULT_RANGE_PRESET,
  generateChartConfig,
  generateDataOnly,
  LiveLimits,
  LiveLimitsByScanner,
  RANGE_PRESETS,
  RangePreset,
  trends_phantom_test_routine,
  scannersAndTrendKeySelected,
  TrendDisplayType,
  TrendsStateInterface,
  trends_survey_test_routine,
} from './helpers'
import { mgBaselineStatus_mgBaselineStatus } from './types/mgBaselineStatus'
import { trendsScannerDetails_scanner } from './types/trendsScannerDetails'
import { trendsScanners_scanner } from './types/trendsScanners'
import { reportHandled } from 'src/app/core/errors/issueTracker'

/** shown under a single scanner's chart when tests since its calibration carry an older baseline */
export interface StaleBaselineNote {
  scannerId: number
  scannerName: string
  staleCount: number
  testCount: number
  calibrationDate: string | null
}

/**
 * shown under the chart when, for the current plot, the live MG baseline of
 * at least one selected scanner could not be fetched (the chart shows the
 * limits stored with the latest test for those scanners)
 */
export interface LiveBaselineUnavailableNote {
  scannerNames: string[]
}

@Component({
  selector: 'app-trends',
  templateUrl: './trends.component.html',
  styleUrls: ['./trends.component.scss'],
})
export class TrendsComponent implements OnInit {
  @Input() type: TrendDisplayType
  @ViewChild('scannerSelect') scannerSelect: ScannerMultiSelectComponent
  @ViewChild('sideControlLeft') sideControlLeft: SideControlsComponent
  @ViewChild('sideControlRight') sideControlRight: SideControlsComponent
  get state(): TrendsStateInterface {
    const selectedScanners = this.scannerSelect?.selected
    const left = {
      selectedRoutine: this.sideControlLeft?.selectedPhantomTestRoutine,
      selectedTrendKey: this.sideControlLeft?.selectedTrendKey,
      showTrendLines: this.sideControlLeft?.showTrendLines,
      showControlLimits: this.sideControlLeft?.showControlLimits,
      showOutliers: this.sideControlLeft?.showOutliers,
      sameYAxis: this.sideControlLeft?.sameYAxis,
      disabled: this.sideControlLeft?.disabled,
      selectedSurveyTestRoutine: this.sideControlLeft
        ?.selectedSurveyTestRoutine,
      selectedSurveyTestField: this.sideControlLeft?.selectedSurveyTestField,
    }
    const right = {
      selectedRoutine: this.sideControlRight?.selectedPhantomTestRoutine,
      selectedTrendKey: this.sideControlRight?.selectedTrendKey,
      showTrendLines: this.sideControlRight?.showTrendLines,
      showControlLimits: this.sideControlRight?.showControlLimits,
      showOutliers: this.sideControlRight?.showOutliers,
      sameYAxis: this.sideControlRight?.sameYAxis,
      disabled: this.sideControlRight?.disabled,
      selectedSurveyTestRoutine: this.sideControlRight
        ?.selectedSurveyTestRoutine,
      selectedSurveyTestField: this.sideControlRight?.selectedSurveyTestField,
    }
    return { selectedScanners, left, right }
  }

  get selectedScanner() {
    return this.state?.selectedScanners?.length > 0
  }

  get firstParamSelected() {
    return this.selectedScanner && this.state?.left?.selectedTrendKey
  }

  constructor(
    private comm: CommService,
    private trendsService: TrendsService,
    private timezone: TimezoneService,
    private terms: TerminologyService,
  ) {}

  // retrieved data
  allScanners: trendsScanners_scanner[]
  allRoomNames: string[]
  scannersDetails: trendsScannerDetails_scanner[]
  routineIntersection: trends_phantom_test_routine[]
  routineUnion: trends_phantom_test_routine[]
  surveyTestRoutineUnion: trends_survey_test_routine[]
  surveyTestRoutineIntersection: trends_survey_test_routine[]
  csvDemoData: CSVDemoData

  // chart options
  _chartOptions: any
  get chartOptions(): any {
    return this._chartOptions
  }
  set chartOptions(val: any) {
    console.debug(JSON.stringify(val))
    this._chartOptions = val
  }

  // methods
  async ngOnInit() {
    await this.fetchScannersList()
  }
  async ngAfterViewInit(): Promise<void> {
    await this.doClientSideWork()
  }

  /** Impure */
  async fetchScannersList() {
    const { scanners, roomNames } = await this.trendsService.allScanners()
    this.allScanners = scanners
    this.allRoomNames = roomNames
  }

  /** Impure */
  async scannerSelectChange() {
    // the live baselines belong to the previous selection
    this.baselineStatus = {}
    // pull new data, refresh client-side stuff
    const {
      scanners,
      routineIntersection,
      routineUnion,
      surveyRoutineIntersection,
      surveyRoutineUnion,
    } = await this.fetchScannerDetails()
    this.scannersDetails = scanners
    this.routineIntersection = routineIntersection
    this.routineUnion = routineUnion
    this.surveyTestRoutineIntersection = surveyRoutineIntersection
    this.surveyTestRoutineUnion = surveyRoutineUnion
    if (routineIntersection.length === 0 && scanners.length > 1) {
      this.comm.alert(
        'info',
        this.terms.apply('These scanners do not have any phantoms in common'),
      )
    }
    await this.doClientSideWork()
  }

  /** Triggered in HTML */
  async sideControlChange() {
    await this.doClientSideWork()
  }

  /** Triggered in HTML */
  onChartClick($event: any) {
    // check if this is a phantom test click
    const possibleTest = $event?.data?.[2]
    if (
      possibleTest &&
      typeof possibleTest?.id === 'number' &&
      possibleTest?.__typename === 'phantom_test'
    ) {
      window.open(`/home/phantomTests/${possibleTest.id}`, '_blank')
    }
  }

  /** Pure */
  private async fetchScannerDetails() {
    // load scanner list from multi-select
    const scannerIds = this.state.selectedScanners.map((s) => s.id)
    // fetch all phantom tests for each scanner
    return await this.trendsService.selectedScannerDetails(scannerIds)
  }

  private async doClientSideWork() {
    await this.debouncedDoClientSideWork()
    this.userHasSelectedScannersAndTrendKey = scannersAndTrendKeySelected(
      this.scannersDetails,
      this.state,
    )
  }

  private executeClientSideWork = async () => {
    if (!this.csvDemoData) {
      this.csvDemoData = await this.trendsService.getCsvDemoData()
    }
    if (
      this.state.selectedScanners.length > 0 &&
      !this.state.left.selectedRoutine &&
      !this.state.left.selectedSurveyTestRoutine &&
      !this.state.right.selectedRoutine &&
      !this.state.right.selectedSurveyTestRoutine
    ) {
      return
    }
    const liveLimits = await this.fetchLiveLimits()
    const { config, rangeWindow } = await generateChartConfig(
      this.scannersDetails,
      this.state,
      this.type,
      this.timezone.cachedTz,
      this.csvDemoData,
      liveLimits,
      this.rangePreset,
    )
    this.chartOptions = config
    this.noTestsInRange = !!rangeWindow && rangeWindow.pointCount === 0
  }

  /**
   * The x-axis view window ("3 months" / "12 months" / "All time"). Every
   * page load starts at All time and the choice lives here only (no storage,
   * no URL, no service), so a refresh or a navigation away and back starts
   * over. A view window only: the statistics, the limits and Download Data
   * always use every test.
   */
  rangePreset: RangePreset = DEFAULT_RANGE_PRESET
  rangePresets = RANGE_PRESETS
  /** the current preset shows none of the plotted tests */
  noTestsInRange = false
  get rangePresetLabel(): string {
    return this.rangePresets.find((p) => p.key === this.rangePreset)?.label
  }

  /** Triggered in HTML */
  async setRangePreset(preset: RangePreset) {
    this.rangePreset = preset
    await this.doClientSideWork()
  }

  /**
   * The server's live MG baselines, per selected scanner. Only a successful
   * answer is memoised: a failed fetch leaves no entry, so the next plot (any
   * re-plot: measurement, limits toggle, range preset, scanner change) retries
   * it instead of falling back to the stored limits for the life of the page.
   */
  baselineStatus: { [scannerId: number]: mgBaselineStatus_mgBaselineStatus[] } = {}
  staleBaselineNote?: StaleBaselineNote
  liveBaselineUnavailable?: LiveBaselineUnavailableNote

  /**
   * For MG routines the chart's baseline and limit lines come from the
   * server's live computation (the scanner's current calibration date), not
   * from the latest test's stored evaluation, so a changed calibration date
   * shows at once. The page still works without them (older server, network
   * error, Hasura's remote-schema cache stale after a deploy): the stored
   * limits are the fallback, the page says so, and the fetch is retried on
   * the next plot. One scanner's failure does not block the others.
   */
  private async fetchLiveLimits(): Promise<LiveLimitsByScanner | undefined> {
    this.staleBaselineNote = undefined
    this.liveBaselineUnavailable = undefined
    const routines = [
      this.state.left.selectedRoutine,
      this.state.right.selectedRoutine,
    ].filter((routine) => routine && routine.phantom === 'mg')
    if (routines.length === 0) {
      return undefined
    }
    const liveLimits: LiveLimitsByScanner = {}
    const unavailable: string[] = []
    for (const scanner of this.state.selectedScanners) {
      const statuses = await this.liveBaselineStatus(scanner)
      if (!statuses) {
        unavailable.push(scanner.name)
      } else {
        const limits = this.liveLimitsOf(scanner, statuses, routines)
        if (Object.keys(limits).length > 0) {
          liveLimits[scanner.id] = limits
        }
      }
    }
    if (unavailable.length > 0) {
      this.liveBaselineUnavailable = { scannerNames: unavailable }
    }
    return liveLimits
  }

  /**
   * A scanner's live baselines, memoised on success. Undefined when the
   * fetch fails: nothing is memoised, so the next plot retries, and this
   * scanner falls back to the limits stored with its latest test.
   */
  private async liveBaselineStatus(
    scanner: trendsScanners_scanner,
  ): Promise<mgBaselineStatus_mgBaselineStatus[] | undefined> {
    if (!this.baselineStatus[scanner.id]) {
      try {
        const statuses = await this.trendsService.mgBaselineStatus(scanner.id)
        this.baselineStatus[scanner.id] = statuses
      } catch (e) {
        console.warn(
          `live baseline unavailable for ${scanner.name}, using stored limits`,
          e,
        )
        reportHandled(e, 'load the live baseline on Trends')
        return undefined
      }
    }
    return this.baselineStatus[scanner.id]
  }

  /** the live control limits of a scanner for the selected MG routines, by limitKey; notes stale tests */
  private liveLimitsOf(
    scanner: trendsScanners_scanner,
    statuses: mgBaselineStatus_mgBaselineStatus[],
    routines: trends_phantom_test_routine[],
  ): LiveLimits {
    const limits: LiveLimits = {}
    routines.forEach((routine) => {
      const status = statuses.find((s) => s.routineId === routine.id)
      if (!status) {
        return
      }
      status.values.forEach((value) => {
        if (typeof value.baseline === 'number') {
          limits[`series.generic_data.sdnr_phantom_data.${value.key}`] = {
            baseline: value.baseline,
            low: value.low ?? undefined,
            high: value.high ?? undefined,
            problemLow: value.problemLow ?? undefined,
            problemHigh: value.problemHigh ?? undefined,
          }
        }
      })
      if (
        this.type === 'singleScanner' &&
        status.staleCount > 0 &&
        !this.staleBaselineNote
      ) {
        this.staleBaselineNote = {
          scannerId: scanner.id,
          scannerName: scanner.name,
          staleCount: status.staleCount,
          testCount: status.testCount,
          calibrationDate: status.calibrationDate,
        }
      }
    })
    return limits
  }

  downloadData() {
    const data = generateDataOnly(
      this.scannersDetails,
      this.state,
      this.type,
      this.timezone.cachedTz,
    )
    const csv = []
    Object.keys(data).forEach((scannerName) => {
      const values = data[scannerName]
      // have to combine data for left and right
      // so, create arrays like this
      const indexedByDate: {
        [key: number]: [number | undefined, number | undefined]
      } = {}
      if (values.leftData) {
        values.leftData.forEach((leftValue) => {
          indexedByDate[leftValue[0]] = [leftValue[1], undefined]
        })
      }
      if (values.rightData) {
        values.rightData.forEach((rightValue) => {
          if (indexedByDate[rightValue[0]]) {
            indexedByDate[rightValue[0]][1] = rightValue[1]
          } else {
            indexedByDate[rightValue[0]] = [undefined, rightValue[1]]
          }
        })
      }
      // points from before the scanner's last calibration are included and
      // flagged, the same split as the chart
      const beforeCalibration = new Set(values.beforeCalibration)
      const calibration =
        typeof values.calibration === 'number'
          ? ` (calibration on ${new Date(values.calibration).toISOString()})`
          : ''
      // push the header row to the csv
      csv.push([
        scannerName,
        this.state.left?.selectedTrendKey?.join(' / '),
        this.state.right?.selectedTrendKey?.join(' / '),
        `before calibration${calibration}`,
      ])
      // push the data rows to the csv
      const sortedDates = Object.keys(indexedByDate).sort()
      sortedDates.forEach((date) => {
        const dateAsISOString = new Date(parseInt(date)).toISOString()
        csv.push([
          dateAsISOString,
          indexedByDate[date][0],
          indexedByDate[date][1],
          beforeCalibration.has(parseInt(date)) ? 'yes' : '',
        ])
      })
    })
    const csvContent = csv.map((row) => row.join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.setAttribute('href', url)
    link.setAttribute('download', 'trends.csv')
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    console.log(csv)
  }

  debouncedDoClientSideWork = debounce(this.executeClientSideWork, 50)

  userHasSelectedScannersAndTrendKey: boolean = false
}
